/**
 * Operação: intervalo e severidade mínima (spec 4.3). Valem no SERVIDOR, no
 * snapshot, e por isso para qualquer firmware — nada vai pela porta USB.
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Group, Select, Stack, Text } from '@mantine/core'

import type { AjustesDoRobo, MudancaDeAjustes, SeveridadeMinima } from '@/api/robo'
import { Linha } from './Linha'
import type { Mensagem } from './mensagens'

const INTERVALOS = [
  { value: '10', label: '10 s' },
  { value: '15', label: '15 s' },
  { value: '30', label: '30 s (padrão)' },
]

const TODAS = 'todas'
const SEVERIDADES = [
  { value: TODAS, label: 'Todas (padrão)' },
  { value: 'warning', label: 'Warning ou mais grave' },
  { value: 'average', label: 'Average ou mais grave' },
  { value: 'critical', label: 'Critical ou mais grave' },
]

export function SecaoOperacao({ ajustes, podeAjustar, ocupado, mensagem, aoSalvar }: {
  ajustes: AjustesDoRobo
  podeAjustar: boolean
  ocupado: boolean
  mensagem: Mensagem | null
  aoSalvar: (mudancas: MudancaDeAjustes) => Promise<void>
}) {
  const intervaloSalvo = String(ajustes.desejado.intervalo_s ?? 30)
  const severidadeSalva = ajustes.desejado.severidade_min ?? TODAS
  const [intervalo, setIntervalo] = useState(intervaloSalvo)
  const [severidade, setSeveridade] = useState<string>(severidadeSalva)
  useEffect(() => setIntervalo(intervaloSalvo), [intervaloSalvo])
  useEffect(() => setSeveridade(severidadeSalva), [severidadeSalva])

  const mudancas: MudancaDeAjustes = {
    ...(intervalo !== intervaloSalvo ? { intervalo_s: Number(intervalo) } : {}),
    ...(severidade !== severidadeSalva
      ? { severidade_min: severidade === TODAS ? null : (severidade as SeveridadeMinima) }
      : {}),
  }
  const mudou = Object.keys(mudancas).length > 0
  const travado = !podeAjustar || ocupado
  const abaixo = ajustes.abaixo_do_limiar

  return (
    <Stack gap="md">
      <Linha rotulo="Intervalo de consulta">
        <Select
          aria-label="Intervalo de consulta"
          data={INTERVALOS} value={intervalo} allowDeselect={false}
          onChange={(v) => v && setIntervalo(v)} disabled={travado} w={200}
        />
        <Text size="xs" c="dimmed">
          De quanto em quanto tempo o robô busca os alertas. Durante uma atualização ou ordem em curso
          ele consulta a cada 5 s, e depois volta a este valor.
        </Text>
      </Linha>
      <Linha rotulo="Severidade mínima">
        <Select
          aria-label="Severidade mínima"
          data={SEVERIDADES} value={severidade} allowDeselect={false}
          onChange={(v) => v && setSeveridade(v)} disabled={travado} w={240}
        />
        <Text size="xs" c="dimmed">
          Alertas abaixo deste nível não aparecem no robô. Ambiente sem coleta continua aparecendo como
          sem dado, nunca como tudo certo.
        </Text>
        {ajustes.desejado.severidade_min && abaixo !== null && (
          <Text size="sm">
            <Text span ff="monospace" size="sm">{abaixo}</Text>
            {abaixo === 1 ? ' alerta ficou abaixo do limiar' : ' alertas ficaram abaixo do limiar'} na última consulta do robô.
          </Text>
        )}
      </Linha>
      <Group gap="sm">
        <Button onClick={() => aoSalvar(mudancas)} disabled={travado || !mudou} loading={ocupado && mudou}>
          Salvar operação
        </Button>
      </Group>
      {mensagem && <Alert color={mensagem.cor} role="status">{mensagem.texto}</Alert>}
    </Stack>
  )
}
